import axios, {
  type AxiosRequestConfig,
  type AxiosResponse,
  type RawAxiosRequestHeaders,
} from 'axios';
import { baseApiEndpoint } from 'config';
import {
  AuthCookieExpiryBufferMs,
  IdentityRefreshTokenFallbackLifetimeMs,
  IdentityRefreshTokenUrl,
  REDIRECT_AFTER_LOGIN_KEY,
  TOKEN,
} from 'features/auth/constants';
import {
  broadcastLogout,
  broadcastTokenRefreshed,
  withAuthRefreshLock,
} from 'features/auth/helpers/crossTab';
import {
  type TIdentityRefreshTokenPayload,
  type TIdentityRefreshTokenResult,
  type TSaveAuthTokensPayload,
} from 'features/auth/types';
import Cookies from 'js-cookie';
import { dayjs } from 'lib/moment/dayjsConfig';
import { convertMillisecondsToDays } from 'lib/moment/utils';
import { getPath } from 'routes/router-paths';
import { routerUtils } from 'shared/utils';

// Bypasses the global axios interceptors: a 401 from the refresh call must fail it, not queue another refresh.
const refreshTokenClient = axios.create({ baseURL: baseApiEndpoint });

/**
 * Saves the current or target URL to sessionStorage to redirect back after logging in.
 * Ignores authentication and public routes (e.g. login, forbidden).
 *
 * @param url - Optional specific URL to save (defaults to current window.location pathname + search + hash).
 */
export const saveRedirectUrl = (url?: string) => {
  if (typeof window === 'undefined') return;

  const { pathname, search, hash } = window.location;

  const targetUrl = url || `${pathname}${search}${hash}`;

  const ignoredPaths = [getPath('loginPage'), getPath('forbiddenPage')];

  const isIgnored = ignoredPaths.some(
    path =>
      targetUrl === path ||
      targetUrl.startsWith(`${path}?`) ||
      targetUrl.startsWith(`${path}/`)
  );

  if (!isIgnored && targetUrl && targetUrl !== '/') {
    sessionStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, targetUrl);
  }
};

/**
 * Retrieves and clears the saved redirect URL from sessionStorage after successful authentication.
 * Falls back to the dashboard path if no saved URL is found.
 *
 * @returns The destination URL to redirect to.
 */
export const getAndClearRedirectUrl = (): string => {
  if (typeof window === 'undefined') return getPath('dashboardPage');

  const redirectUrl = sessionStorage.getItem(REDIRECT_AFTER_LOGIN_KEY);
  if (redirectUrl) {
    sessionStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
    return redirectUrl;
  }
  return getPath('dashboardPage');
};

/**
 * Sets an authentication token in browser cookies.
 *
 * @param tokenType - Type of token (ACCESS_TOKEN or REFRESH_TOKEN).
 * @param token - Token string value.
 * @param expiration - Lifetime in milliseconds; the cookie expires `AuthCookieExpiryBufferMs` earlier when the
 *   lifetime allows it. If omitted, sets a session cookie (cleared on browser close).
 */
export const setAuthToken = (
  tokenType: TOKEN,
  token: string,
  expiration?: number
) => {
  // `expires: 0` would make js-cookie expire the cookie at once, so a session cookie gets no `expires` at all
  if (expiration === undefined) {
    Cookies.set(tokenType, token);
    return;
  }

  const lifetimeMs =
    expiration > AuthCookieExpiryBufferMs
      ? expiration - AuthCookieExpiryBufferMs
      : expiration;

  Cookies.set(tokenType, token, {
    expires: convertMillisecondsToDays(lifetimeMs),
  });
};

/**
 * Retrieves an authentication token from browser cookies.
 * Returns null if the cookie has expired or does not exist.
 *
 * @param tokenType - Type of token to retrieve.
 * @returns The token string or null.
 */
export const getAuthToken = (tokenType: TOKEN): string | null => {
  return Cookies.get(tokenType) ?? null;
};

/**
 * Clears all authentication tokens from browser cookies.
 */
export const clearAuthToken = () => {
  Cookies.remove(TOKEN.ACCESS_TOKEN);
  Cookies.remove(TOKEN.REFRESH_TOKEN);
};

// Subscriber pattern: collapses concurrent 401 requests within the same tab into a single refresh call.

let isAlreadyFetchingAccessToken = false;
// eslint-disable-next-line @typescript-eslint/ban-types
let subscribers: Function[] = [];

/**
 * Notifies all waiting subscribers with the newly fetched access token.
 *
 * @param token - The newly refreshed access token.
 */
const onAccessTokenFetched = (token: string) => {
  subscribers = subscribers.filter(callback => callback(token));
};

/**
 * Adds a callback subscriber to the queue to await a refreshed access token.
 *
 * @param callback - The callback function to invoke once token is fetched.
 */
// eslint-disable-next-line @typescript-eslint/ban-types
const addSubscriber = (callback: Function) => {
  subscribers.push(callback);
};

/**
 * Decodes and returns the expiration timestamp (ms) from a JWT string, or null if invalid.
 *
 * @param token - The JWT string to parse.
 * @returns Expiration timestamp in milliseconds or null if decoding fails.
 */
export const getAccessTokenExpiry = (token: string): number | null => {
  try {
    const payloadSegment = token.split('.')[1];
    if (!payloadSegment) return null;

    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(char => '%' + char.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    const payload = JSON.parse(json) as { exp?: number };

    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

/**
 * Timestamp (ms) of a date sent by the identity service, or null when it sent none.
 * .NET may send the date without an offset; it is UTC either way.
 */
export const parseIdentityUtcDate = (value?: string | null) =>
  value ? dayjs.utc(value).valueOf() : null;

/**
 * Saves the session tokens in cookies: the access token lives until its JWT `exp`,
 * the refresh token until `refreshTokenExpiresAt`.
 *
 * @param payload - The tokens; `refreshTokenExpiresAt` falls back to
 *   `IdentityRefreshTokenFallbackLifetimeMs` when the server does not return one.
 */
export const saveAuthTokens = ({
  [TOKEN.ACCESS_TOKEN]: accessToken,
  [TOKEN.REFRESH_TOKEN]: refreshToken,
  refreshTokenExpiresAt,
}: TSaveAuthTokensPayload) => {
  const now = Date.now();
  const accessTokenExpiresAt = getAccessTokenExpiry(accessToken);

  setAuthToken(
    TOKEN.ACCESS_TOKEN,
    accessToken,
    accessTokenExpiresAt ? accessTokenExpiresAt - now : undefined
  );
  setAuthToken(
    TOKEN.REFRESH_TOKEN,
    refreshToken,
    refreshTokenExpiresAt
      ? refreshTokenExpiresAt - now
      : IdentityRefreshTokenFallbackLifetimeMs
  );
};

/**
 * Cleans up the current tab's authentication session, broadcasts logout to other tabs,
 * saves the current URL for post-login redirect, and navigates to the login page.
 */
export const handleAuthFailure = () => {
  subscribers = []; // Clear queue to avoid memory leaks
  isAlreadyFetchingAccessToken = false;
  saveRedirectUrl();
  clearAuthToken();
  broadcastLogout();
  routerUtils.navigate(getPath('loginPage'));
};

/**
 * Refreshes the access token via identity `POST /identity/refresh-token`, protected by Web Locks
 * to ensure only one tab performs the network refresh call at a time.
 * If another tab already finished refreshing while waiting for the lock, reuses the new token.
 * The identity service rotates the refresh token on every call, so both cookies are replaced.
 *
 * @param staleToken - The token held prior to acquiring the lock (used to detect if another tab already refreshed);
 *   null when the failed request was sent without one.
 * @returns The newly refreshed access token or null if refresh failed.
 */
export const refreshAccessToken = (
  staleToken: string | null
): Promise<string | null> =>
  withAuthRefreshLock(async () => {
    // Another tab might have finished refreshing while we waited for the lock -> reuse the new token
    const currentToken = getAuthToken(TOKEN.ACCESS_TOKEN);
    if (currentToken && currentToken !== staleToken) {
      return currentToken;
    }

    const refreshToken = getAuthToken(TOKEN.REFRESH_TOKEN);
    if (!refreshToken) return null;

    try {
      const { data } =
        await refreshTokenClient.post<TIdentityRefreshTokenResult>(
          IdentityRefreshTokenUrl,
          { refreshToken } satisfies TIdentityRefreshTokenPayload
        );
      if (!data.accessToken || !data.refreshToken) return null;

      saveAuthTokens({
        [TOKEN.ACCESS_TOKEN]: data.accessToken,
        [TOKEN.REFRESH_TOKEN]: data.refreshToken,
        refreshTokenExpiresAt: parseIdentityUtcDate(
          data.refreshTokenExpiration
        ),
      });
      broadcastTokenRefreshed();

      return data.accessToken;
    } catch {
      return null;
    }
  });

/**
 * Handles token refresh when receiving a 401 Unauthorized status from an API request.
 * - Subscriber pattern: Queues and retries concurrent 401 requests within the current tab.
 * - Web Locks (via refreshAccessToken): Ensures cross-tab synchronization with only 1 active refresh call.
 *
 * @param originalRequest - The Axios request configuration of the failed request.
 * @returns A Promise resolving to the retried Axios response or undefined if refresh cannot proceed.
 */
export const handleRefreshToken = (
  originalRequest: AxiosRequestConfig
): Promise<AxiosResponse> | undefined => {
  const retryOriginalRequest: Promise<AxiosResponse> = new Promise(resolve => {
    addSubscriber((token: string) => {
      (originalRequest.headers as RawAxiosRequestHeaders).Authorization =
        'Bearer ' + token;
      return resolve(axios(originalRequest));
    });
  });

  // No refresh token available -> logout and redirect to login page
  if (!getAuthToken(TOKEN.REFRESH_TOKEN)) {
    handleAuthFailure();
    return;
  }

  // Only invoke refresh once even if multiple requests are awaiting
  if (!isAlreadyFetchingAccessToken) {
    isAlreadyFetchingAccessToken = true;

    // The token that just received a 401 (from original request header) to compare after acquiring lock
    const authHeader = (
      originalRequest.headers as RawAxiosRequestHeaders | undefined
    )?.Authorization;
    const failedToken =
      typeof authHeader === 'string' ? authHeader.replace('Bearer ', '') : null;

    void refreshAccessToken(failedToken).then(newToken => {
      isAlreadyFetchingAccessToken = false;
      if (newToken) {
        onAccessTokenFetched(newToken);
      } else {
        handleAuthFailure();
      }
    });
  }

  return retryOriginalRequest;
};
